import { useEffect, useState } from 'react'
import { AudioLines, MessagesSquare, X } from 'lucide-react'
import type { DispatchHelpSession } from '@numeo/types'
import { EmptyState, IconButton, Spinner } from 'components/ds'
import { formatAge } from 'utils/format-relative-time'
import { fetchSessions } from '../services/dispatchHelpApi'
import { sessionOwnerLabel } from '../sessionLabel'

/**
 * Right slide-over listing this owner's OTHER conversations — the same overlay shape as EmailsPanel,
 * inside the `relative` ChatThread pane.
 *
 * An operator picking up a session usually wants the history behind it: has this driver asked before,
 * did someone already help them, is this the third attempt at the same lane. That context lives in
 * their other tabs, which the main list buries among every other company's.
 *
 * Server-side this is the ordinary session list with `ownerId` set, so it reuses the existing keyset
 * query and adds none of its own.
 */
export function OwnerSessionsPanel({
    ownerId,
    currentTabId,
    open,
    onClose,
    onSelect,
}: {
    ownerId: string
    currentTabId: string
    open: boolean
    onClose: () => void
    onSelect: (tabId: string) => void
}) {
    const [sessions, setSessions] = useState<DispatchHelpSession[]>([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!open) return
        setLoading(true)
        fetchSessions({ ownerId, limit: 30 })
            .then((page) => setSessions(page.sessions.filter((s) => s.tabId !== currentTabId)))
            .catch(() => setSessions([]))
            .finally(() => setLoading(false))
    }, [open, ownerId, currentTabId])

    if (!open) return null

    return (
        <div className="absolute inset-0 z-20 flex">
            <div className="hidden flex-1 bg-slate-900/30 backdrop-blur-[1px] transition-opacity dark:bg-black/60 sm:block" onClick={onClose} />
            <div className="flex h-full w-full flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:w-[400px]">
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Other conversations</span>
                    <IconButton variant="ghost" size="sm" icon={<X className="h-4 w-4" />} onClick={onClose} aria-label="Close" />
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex h-full items-center justify-center">
                            <Spinner />
                        </div>
                    ) : sessions.length === 0 ? (
                        <EmptyState icon={<MessagesSquare className="h-6 w-6" />} title="No other conversations" description="This is their only session." />
                    ) : (
                        sessions.map((s) => (
                            <button
                                key={s.tabId}
                                type="button"
                                onClick={() => {
                                    onSelect(s.tabId)
                                    onClose()
                                }}
                                className="flex w-full flex-col gap-0.5 border-b border-slate-100 px-4 py-2.5 text-left hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/60"
                            >
                                <span className="flex items-center gap-1.5">
                                    <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{s.title || 'Conversation'}</span>
                                    {s.hasVoice && <AudioLines className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" />}
                                    <span className="ml-auto shrink-0 text-xs text-slate-400 dark:text-slate-500">
                                        {formatAge(s.lastActivityAt || s.createdAt)}
                                    </span>
                                </span>
                                <span className="truncate text-xs text-slate-500 dark:text-slate-400">{sessionOwnerLabel(s)}</span>
                            </button>
                        ))
                    )}
                </div>
            </div>
        </div>
    )
}
